<script setup>
import { computed, useAttrs } from 'vue'

const SIZE_CLASSES = {
    small: 'base-button-small',
    medium: 'base-button-medium',
    xsmall: 'base-button-xsmall',
    xlarge: 'base-button-xlarge',
    iconOnly: 'base-button-icon-only',
    hero: 'base-button-hero',
}

const TYPE_CLASSES = {
    primary: 'base-button-primary',
    primaryFlat: 'base-button-primary-flat',
    secondary: 'base-button-secondary',
    secondaryPink: 'base-button-secondary-pink',
    highlight: 'base-button-highlight',
    nadir: 'base-button-nadir',
    blue: 'base-button-blue',
    facebook: 'base-button-facebook',
    twitter: 'base-button-twitter',
    google: 'base-button-google',
    apple: 'base-button-apple',
    tag: 'base-button-tag',
    tagSecondary: 'base-button-tag-secondary',
    tagBlack: 'base-button-tag-black',
    black: 'base-button-black',
    white: 'base-button-white',
    follow: 'base-button-follow',
    transparent: 'base-button-transparent',
    transparentWhite: 'base-button-transparent-white',
    dots: 'base-button-dots',
    cross: 'base-button-cross',
}

const MODIFIER_CLASSES = {
    dots: 'base-button-dots',
    fill: 'base-button-fill',
    autoWidth: 'base-button-auto-width',
    noMargins: 'base-button-no-margins',
    roundCorners: 'base-button-round-corners',
}

const props = defineProps({
    icon: {
        type: [String, Boolean],
        default: '',
    },
    size: {
        type: String,
        default: 'large',
    },
    buttonType: {
        type: String,
        default: 'primary', // tag, primary, primaryFlat, secondary, transparent, tagSecondary, facebook, twitter, highlight, tagBlack, black, nadir, blue, follow, secondaryPink
    },
    active: {
        type: Boolean,
        default: false,
    },
    modifiers: {
        type: Object,
        default: () => ({
            dots: false,
            autoWidth: false,
            fill: false,
            noMargins: false,
            roundCorners: false,
        }),
    },
})

const attrs = useAttrs()

const isLabel = computed(() => !attrs.onClick && attrs.type !== 'submit')

const sizeClass = computed(() => SIZE_CLASSES[props.size])

const typeClass = computed(() => TYPE_CLASSES[props.buttonType])

const modifierClasses = computed(() =>
    Object.keys(MODIFIER_CLASSES)
        .filter(name => props.modifiers[name])
        .map(name => MODIFIER_CLASSES[name])
)

const iconClasses = computed(() => [
    'base-button-icon',
    props.icon === 'signal' && 'base-button-icon-signal',
    props.size === 'iconOnly' && 'base-button-icon-only',
])
</script>
<template>
    <button
        :class="[
            'base-button',
            sizeClass,
            typeClass,
            modifierClasses,
            isLabel && 'base-button-cannot-be-hovered',
            active && 'base-button-active',
        ]"
    >
        <span class="base-button-helper" />
        <BaseIcon
            v-if="icon"
            :name="icon"
            :class="iconClasses"
        />
        <span
            v-if="size !== 'icon'"
            class="base-button-main"
        >
            <slot />
        </span>
    </button>
</template>

<style lang="scss" scoped>
@use '@design' as *;

@mixin buttonHover {
    &::after {
        @include fill;

        z-index: $layer-negative-z-index;
        content: '';
        background: $c-button-bg;
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
    }

    &:hover::after {
        opacity: 1;
    }
}

@mixin brandedButton($color) {
    @include baseShadow;

    position: relative;
    z-index: $layer-tooltip-z-index;
    padding: 0 $s-xxl;
    overflow: hidden;
    font-weight: 600;
    color: $c-white;
    word-spacing: 2px;
    letter-spacing: 1.5px;
    background: $color;

    &::before,
    &::after {
        z-index: $layer-negative-z-index;
        content: '';
        opacity: 0;
        transition: opacity 0.3s ease-in-out;

        @include fill;
    }

    &::before {
        background: lighten($color, 10%);
    }

    &::after {
        background: darken($color, 10%);
    }

    &:hover::before {
        opacity: 1;
    }

    &:active::after,
    &.base-button-active::after {
        opacity: 1;
    }
}

// Top-level selectors stay wrapped in `:where()` so the scoping attribute does
// not raise their specificity above the overrides callers pass in via `:class`.
// See "Component authoring standard > Styles" in the README.
:where(.base-button-icon) {
    position: absolute;
    top: 50%;
    left: $s-m;
    padding-right: $s-xxs;
    margin-top: -0.6rem;
    fill: $c-white;

    @include square(1.2rem);
}

:where(.base-button-icon-only) {
    @include center;

    margin-top: 0;
}

:where(.base-button-icon-signal) {
    left: 1.5rem;
    margin-top: -0.6rem;

    @include square(1.2rem);

    + .base-button-main {
        padding-right: $s-xxl;
        padding-left: $s-xxl + $s-m;
    }
}

:where(.base-button-small .base-button-icon) {
    left: 0.5em;

    + .base-button-main {
        padding-right: 0;
        padding-left: 0;
        text-indent: 0.8rem;
    }
}

:where(.base-button-medium .base-button-icon) {
    left: 0.5em;

    + .base-button-main {
        padding-right: 0;
        padding-left: 0;
        text-indent: 1.1rem;
    }
}

:global(.base-button) {
    margin-bottom: $button-margin;
    font-size: 14px;
}

:where(.base-button) {
    // Base button styles
    position: relative;
    z-index: $layer-page-z-index;
    width: 100%;
    height: $size-touch-target;
    padding: $size-button-padding;
    font-weight: 600;
    cursor: pointer;
    outline: 0;
    background: none;
    border: 0;
    border-radius: $button-border-radius;
    transition: transform 0.1s ease-in-out;

    &:active {
        transform: scale(0.95);
    }

    &::before,
    &::after {
        border-radius: $button-border-radius;
    }

    &:active,
    &:focus {
        outline: none;
        border: none;
    }

    @include desktop {
        width: auto;
        min-width: 160px;
        height: $s-xxl;
    }

    &.base-button-small,
    &.base-button-medium {
        display: flex;
        align-items: center;
        justify-content: center;
        width: auto;
        min-width: 76px;
        height: $s-xl;
        padding: 0 $s-s;
        margin-bottom: 0;
        font-size: 10px;
        font-weight: 700;
        white-space: nowrap;
    }

    &.base-button-medium {
        font-size: 11px;
    }

    &.base-button-xsmall {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 18px;
        font-size: 0.5em;
        font-weight: 500;
        white-space: nowrap;
    }

    &.base-button-xlarge {
        height: auto;
        padding: $s-l;
    }

    &.base-button-icon-only {
        min-width: 0;
    }

    // Button type styles
    &.base-button-primary {
        color: $c-button-text;
        letter-spacing: 1px;

        @include buttonHover;
        @include baseShadow;

        &::before {
            z-index: $layer-negative-z-index;
            content: '';
            background: $c-button-bg;

            @include fill;
        }
        &::after {
            background: $c-button-bg-dark;
        }

        &:active::after,
        &.base-button-active::after {
            background: $c-highlight;
        }
    }

    &.base-button-primary-flat {
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        color: $c-button-text;
        text-transform: none;
        letter-spacing: 1px;
        border: none;

        &::before {
            z-index: $layer-negative-z-index;
            content: '';
            background: $c-razzmatazz;

            @include fill;
        }

        &:hover::before {
            opacity: 0.9;
        }

        &:active::before,
        &.base-button-active::before {
            background: darken($c-razzmatazz, 10%);
        }
    }

    &.base-button-secondary {
        color: $c-button-text;
        letter-spacing: 1px;
        background-clip: padding-box;
        border-color: transparent;

        &::before {
            z-index: $layer-negative-z-index;
            content: '';
            border: 3px solid $c-cerise-red;

            @include fill;
        }

        @include buttonHover;
        @include baseShadow;

        &:active::after,
        &.base-button-active::after {
            background: $c-highlight;
        }
    }

    &.base-button-highlight {
        color: $c-button-text;
        letter-spacing: 1px;
        border: none;

        &::before {
            z-index: $layer-negative-z-index;
            content: '';
            background: $c-highlight;

            @include fill;
        }

        &::after {
            background: lighten($c-highlight, 10%);
        }

        @include buttonHover;
        @include baseShadow;

        &:active::after,
        &.base-button-active::after {
            background: $c-highlight;
        }
    }

    &.base-button-nadir {
        color: $c-button-text;
        border: none;

        &::before,
        &::after {
            content: '';
        }

        &::before {
            z-index: $layer-negative-z-index;
            content: '';
            background: $c-nevada-grey;

            @include fill;
        }

        &:hover {
            &::before {
                background: lighten($c-nevada-grey, 10%);
            }
        }

        &:active::after,
        &.base-button-active::after {
            background: $c-nevada-grey;
        }
    }

    &.base-button-blue {
        @include brandedButton($c-azure-radiance);
    }

    &.base-button-facebook {
        @include brandedButton($c-facebook);
    }

    &.base-button-twitter {
        @include brandedButton($c-twitter);
    }

    &.base-button-google {
        @include brandedButton($c-google);
    }

    &.base-button-apple {
        @include brandedButton($c-apple-grey);
    }

    &.base-button-tag {
        color: $c-white;

        &::after {
            z-index: $layer-negative-z-index;
            content: '';
            background-color: $c-bali-hai;
            border-radius: 2px;
            opacity: 0.1;

            @include fill;
        }

        &:hover::after {
            opacity: 0.4;
        }

        &:active::after,
        &.base-button-active::after {
            background-color: $c-highlight;
            opacity: 1;
        }
    }

    &.base-button-tag-secondary {
        z-index: $layer-page-z-index;
        color: $c-white;
        background: none;

        &::before {
            z-index: $layer-negative-z-index;
            box-sizing: border-box;
            content: '';
            border: 2px solid $c-grey-heavy-metal;

            @include fill;
        }

        @include buttonHover;

        &:active::after,
        &.base-button-active::after {
            background: $c-highlight;
        }
    }

    &.base-button-black {
        @include baseShadow;

        z-index: $layer-page-z-index;
        color: $c-white;

        &::before {
            z-index: $layer-negative-z-index;
            box-sizing: border-box;
            content: '';
            background: $c-black;

            @include fill;
        }

        @include buttonHover;

        &::after {
            background: $c-shuttle-gray;
        }

        &:active::after,
        &.base-button-active::after {
            background: $c-shuttle-gray;
        }
    }

    &.base-button-white {
        @include baseShadow;

        z-index: $layer-page-z-index;
        color: $c-black;

        &::before {
            z-index: $layer-negative-z-index;
            box-sizing: border-box;
            content: '';
            background: $c-white;

            @include fill;
        }

        @include buttonHover;

        &::after {
            background: darken($c-white, 10%);
        }

        &:active::after,
        &.base-button-active::after {
            background: darken($c-white, 10%);
        }
    }

    &.base-button-tag-black {
        @include baseShadow;

        z-index: $layer-page-z-index;
        color: $c-white;

        &::before {
            z-index: $layer-negative-z-index;
            box-sizing: border-box;
            content: '';
            border: 2px solid $c-shuttle-gray;

            @include fill;
        }

        @include buttonHover;

        &::after {
            background: $c-shuttle-gray;
        }

        &:active::after,
        &.base-button-active::after {
            background: $c-shuttle-gray;
        }
    }

    &.base-button-follow {
        font-weight: 600;
        color: $c-white;
        text-transform: none;
        letter-spacing: 0.5px;
        background: transparent;
        border: 2px solid $c-white;
        border-radius: 20px;
        transition: all 0.2s ease-in-out;

        &::before,
        &::after {
            display: none;
        }

        &:hover {
            opacity: 0.8;
        }

        &.base-button-active {
            color: $c-razzmatazz;
            border-color: $c-razzmatazz;
        }
    }

    &.base-button-secondary-pink {
        font-weight: 600;
        color: $c-white;
        text-transform: none;
        letter-spacing: 0.5px;
        background: $c-razzmatazz;
        border: none;
        border-radius: $s-xs;
        transition: all 0.2s ease-in-out;

        &::before,
        &::after {
            display: none;
        }

        &:hover {
            background: darken($c-razzmatazz, 5%);
        }

        &:active,
        &.base-button-active {
            background: darken($c-razzmatazz, 10%);
        }
    }

    // Button modifiers

    &.base-button-auto-width {
        width: auto;
        min-width: unset;
    }

    &.base-button-fill {
        width: 100%;
    }

    &.base-button-transparent {
        font-weight: normal;
        color: $c-text-faded;
        text-transform: none;
        background: none;

        &-white {
            color: $c-white;
            text-transform: uppercase;
            transition: background 0.3s ease-out;
            &:hover {
                background: $c-highlight;
            }
        }

        &::before {
            background: none;
        }
        &::after {
            background: none;
        }

        &:hover {
            color: $c-highlight;
            .base-button-icon {
                fill: $c-highlight;
            }
        }
    }

    &.base-button-dots {
        min-width: 28px;
        &::before {
            display: block;
            content: '';
            background-color: $c-white;
            border-radius: 50%;
            box-shadow: 7px 0 $c-white, -7px 0 $c-white;

            @include square(4px);
            @include center;
        }
    }

    &.base-button-cross {
        min-width: 28px;
        .base-button-helper {
            display: block;

            &::before,
            &::after {
                display: block;
                width: 9px;
                height: 2px;
                content: '';
                background-color: $c-white;
                transform-origin: center;

                @include center;
            }

            &::after {
                transform: translate(-50%, -50%) rotate(90deg);
            }
        }
    }

    &.base-button-cannot-be-hovered {
        pointer-events: none;
        cursor: default;
    }

    &.base-button-no-margins {
        margin: 0;
    }

    &.base-button-round-corners {
        overflow: hidden;
        border-radius: $size-radius-l;
    }

    // Brand CTA (Figma 816:370): taller, roomier and more rounded than the
    // default button.
    // Deliberately declared last. `size` and `buttonType` are orthogonal props
    // but compile to sibling compound selectors of equal specificity
    // (`.base-button.base-button-hero` vs `.base-button.base-button-primary-flat`),
    // so source order decides. Every type rule above sets `font-weight`, and
    // `.base-button-transparent` and `.base-button-round-corners` set `font-weight`
    // and `border-radius` — declaring `hero` here is what lets it win without a
    // specificity hack at the call site.
    &.base-button-hero {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 52px;
        padding: 0 $s-xxl;
        font-size: 18px;
        font-weight: 700;
        white-space: nowrap;
        border-radius: $size-radius-m;
    }

    &:disabled {
        cursor: not-allowed;
        background: $c-button-disabled-bg;
        border: 1px solid $c-shark;
        &::before,
        &::after {
            background: $c-button-disabled-bg;
        }
    }
}
</style>
